<script lang="ts">
  import { app } from "../../state/app.svelte";
  import { sendUserMessage } from "../../state/chat";
  import { runTestSuite } from "../../state/testSuite";
  import { tokenLimitsFor } from "../../state/config";
  import { i18n } from "../../i18n/i18n.svelte";
  import { exportConversationAsDoc } from "../../api/exportConversation";
  import { parseFileToMarkdown, progressLabel } from "../../api/epflServer";
  import { fileToBase64, keepsOriginal } from "../../api/docxEdits";
  import type { AttachedDoc } from "../../db/types";
  import MessageBubble from "./MessageBubble.svelte";
  import AgentActivity from "./AgentActivity.svelte";

  const fr = {
    close: "Fermer",
    readFailed: "Lecture impossible",
    receiving: "réception…",
    missingConfig: "Configuration manquante",
    tooLarge: (name: string, tokens: string, limit: string) =>
      `« ${name} » est trop volumineux (~${tokens} tokens) : le total dépasserait ${limit}. ` +
      `Ajoute-le plutôt comme document indexé dans l'onglet Docs.`,
    conversation: "Conversation",
    phaseSearching: "Recherche des passages pertinents…",
    phaseAnswering: "Rédaction de la réponse…",
    phasePreparing: "Préparation…",
    suiteRunning: (i: number, n: number) => `Questions en cours… ${i} / ${n}`,
    suiteRun: (n: number) => `▶ Lancer les questions automatiques${n > 0 ? ` (${n})` : ""}`,
    suiteNone: "Aucune question définie. Ouvre",
    suiteNoneEnd: "pour en ajouter.",
    suiteExplain: (n: number) =>
      `Envoie séquentiellement les ${n} question${n > 1 ? "s" : ""} automatiques. ` +
      `Si tu as écrit du texte dans la zone de saisie, il sera ajouté en préambule à la première question. ` +
      `Tu peux les modifier ou réordonner dans`,
    suitePath: "Configuration → Questions automatiques",
    exportEmpty: "La conversation est vide",
    exportTitle: "Télécharger la conversation au format Word (.doc)",
    exporting: "Export en cours…",
    exportBtn: "⬇ Exporter (.doc)",
    newConversation: "Nouvelle conversation",
    noActiveDoc: "Aucun document actif.",
    noActiveDocHint: "Tu peux coller un texte directement dans le chat pour le travailler, ou activer un document dans l'onglet",
    docsTabSuffix: "",
    askActive: "Pose une question sur les documents actifs.",
    attachmentAlt: "pièce jointe",
    remove: "Retirer",
    removeImage: "Retirer l'image",
    removeDoc: "Retirer le document",
    extracting: "extraction…",
    docsBudget: (used: string, limit: string) => `docs : ${used} / ${limit} tokens`,
    placeholderNoDocs: "Posez votre question ou collez un texte…",
    placeholder: "Posez votre question…",
    attachTitle: "Joindre une image ou un document",
    attachAria: "Joindre une pièce jointe",
    fullTextNoDocs: "Mode FullText — aucun document actif",
    fullTextOn: (n: number) => `FullText : les ${n} document(s) actif(s) sont envoyés en entier`,
    fullTextOff: "FullText : envoyer les documents actifs en entier (au lieu des seuls passages pertinents)",
    epflOn: "Recherche EPFL activée (site + ServiceNow KB + tickets résolus)",
    epflOnNoSession: "Recherche EPFL activée — connexion EPFL requise (onglet Config)",
    epflOff: "Recherche EPFL : inclure le site epfl.ch + ServiceNow KB + tickets résolus",
    lexOn: "LEX seulement : la recherche EPFL ne porte que sur les textes de Polylex (LEX, directives, annexes, lois fédérales)",
    lexOnNoSession: "LEX seulement — connexion EPFL requise (onglet Config)",
    lexOff: "LEX seulement : limiter la recherche EPFL aux textes réglementaires de Polylex",
    webOn: "Recherche web activée (via Exa)",
    webOff: "Recherche web : inclure des résultats du web (via Exa)",
    thinkingOn: "Réflexion activée : l'assistant mène lui-même ses recherches, en plusieurs passes si besoin, et réfléchit avant de répondre",
    thinkingOff: "Réflexion : laisser l'assistant mener ses recherches et réfléchir avant de répondre (plus approfondi, parfois plus lent)",
    thinking: "Réflexion",
    helpTitle: "Aide : à quoi servent les bulles, les documents et les citations",
    help: "Aide",
    stop: "Arrêter la réponse",
    send: "Envoyer",
  };
  const en: typeof fr = {
    close: "Close",
    readFailed: "Could not read the file",
    receiving: "uploading…",
    missingConfig: "Settings missing",
    tooLarge: (name, tokens, limit) =>
      `"${name}" is too large (~${tokens} tokens): the total would exceed ${limit}. ` +
      `Add it as an indexed document in the Docs tab instead.`,
    conversation: "Conversation",
    phaseSearching: "Searching for relevant passages…",
    phaseAnswering: "Writing the answer…",
    phasePreparing: "Preparing…",
    suiteRunning: (i, n) => `Running questions… ${i} / ${n}`,
    suiteRun: (n) => `▶ Run automatic questions${n > 0 ? ` (${n})` : ""}`,
    suiteNone: "No questions defined. Open",
    suiteNoneEnd: "to add some.",
    suiteExplain: (n) =>
      `Sends the ${n} automatic question${n > 1 ? "s" : ""} one after the other. ` +
      `Any text typed in the input box is prepended to the first question. ` +
      `You can edit or reorder them in`,
    suitePath: "Settings → Automatic questions",
    exportEmpty: "The conversation is empty",
    exportTitle: "Download the conversation as a Word file (.doc)",
    exporting: "Exporting…",
    exportBtn: "⬇ Export (.doc)",
    newConversation: "New conversation",
    noActiveDoc: "No active document.",
    noActiveDocHint: "You can paste a text straight into the chat to work on it, or activate a document in the",
    docsTabSuffix: " tab",
    askActive: "Ask a question about the active documents.",
    attachmentAlt: "attachment",
    remove: "Remove",
    removeImage: "Remove the image",
    removeDoc: "Remove the document",
    extracting: "extracting…",
    docsBudget: (used, limit) => `docs: ${used} / ${limit} tokens`,
    placeholderNoDocs: "Ask your question or paste a text…",
    placeholder: "Ask your question…",
    attachTitle: "Attach an image or a document",
    attachAria: "Add an attachment",
    fullTextNoDocs: "FullText mode — no active document",
    fullTextOn: (n) => `FullText: the ${n} active document(s) are sent in full`,
    fullTextOff: "FullText: send the active documents in full (instead of only the relevant passages)",
    epflOn: "EPFL search on (website + ServiceNow KB + resolved tickets)",
    epflOnNoSession: "EPFL search on — EPFL sign-in required (Settings)",
    epflOff: "EPFL search: include the epfl.ch website + ServiceNow KB + resolved tickets",
    lexOn: "LEX only: the EPFL search covers only the Polylex texts (LEX, directives, annexes, federal law)",
    lexOnNoSession: "LEX only — EPFL sign-in required (Settings)",
    lexOff: "LEX only: restrict the EPFL search to the Polylex regulations",
    webOn: "Web search on (via Exa)",
    webOff: "Web search: include results from the web (via Exa)",
    thinkingOn: "Thinking on: the assistant runs its own searches, in several passes if needed, and thinks before answering",
    thinkingOff: "Thinking: let the assistant run its own searches and think before answering (more thorough, sometimes slower)",
    thinking: "Thinking",
    helpTitle: "Help: what the toggles, documents and citations are for",
    help: "Help",
    stop: "Stop the answer",
    send: "Send",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  let input = $state("");
  let textarea: HTMLTextAreaElement;
  let scrollArea: HTMLDivElement;
  let fileInput: HTMLInputElement;

  // Pending attachments for the next message. Images are base64 data URLs;
  // docs carry their full extracted markdown (parsed via the same /parse path
  // as the index) and a chars/4 token estimate.
  let pendingImages = $state<string[]>([]);
  let pendingDocs = $state<AttachedDoc[]>([]);
  let attaching = $state(false);
  let attachDetail = $state<string | null>(null);
  let attachError = $state<string | null>(null);
  let dragOver = $state(false);

  // Documents (pdf, docx, …) plus images. Same extensions the docs tab accepts.
  const ACCEPT = "image/*,.pdf,.docx,.doc,.odt,.xlsx,.xls,.ods,.csv,.tsv,.html,.htm,.txt,.md";

  // Inline docs are capped cumulatively across the whole conversation: the doc
  // text is replayed into context on every turn, so the budget is the sum of
  // already-sent docs plus what's staged in the composer.
  // Le plafond suit le seuil « lent » du modèle (tokenLimitsFor, 250 000 pour
  // GLM-5.3-Flash) et non sa fenêtre (1 048 576) : ce qui gêne, c'est le prefill
  // rejoué à CHAQUE question (~20 s à 250 000 jetons). Il valait 50 000 du
  // temps des modèles à 128-256 k de fenêtre.
  const DOC_TOKEN_LIMIT = $derived(tokenLimitsFor(app.config?.rcpModel ?? "").soft);
  const convDocTokens = $derived(
    app.currentMessages.reduce(
      (sum, m) => sum + (m.attachedDocs?.reduce((s, d) => s + d.tokens, 0) ?? 0),
      0,
    ),
  );
  const pendingDocTokens = $derived(
    pendingDocs.reduce((s, d) => s + d.tokens, 0),
  );
  const totalDocTokens = $derived(convDocTokens + pendingDocTokens);
  const hasPending = $derived(pendingImages.length > 0 || pendingDocs.length > 0);

  // Auto-focus the composer when:
  //  - the panel mounts,
  //  - the user switches conversation (or starts a new one),
  //  - the assistant has finished streaming a response.
  // Reactive on isStreaming + currentConversationId; the queueMicrotask wait
  // lets Svelte finish DOM updates (textarea may be re-enabled this tick).
  $effect(() => {
    void app.isStreaming;
    void app.currentConversationId;
    if (!app.isStreaming && textarea) {
      queueMicrotask(() => textarea?.focus());
    }
  });

  // Stick-to-bottom behaviour: auto-scroll only if the user hasn't scrolled up.
  // Flipped to false as soon as the user scrolls more than ~40px above the end,
  // and back to true when they return near the bottom.
  const STICKY_THRESHOLD = 40;
  let stickToBottom = $state(true);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const q = input.trim();
    if ((!q && !hasPending) || app.isStreaming || attaching) return;
    const images = pendingImages;
    const docs = pendingDocs;
    input = "";
    pendingImages = [];
    pendingDocs = [];
    attachError = null;
    autoResize();
    stickToBottom = true;
    scrollToBottom();
    await sendUserMessage(q, { images, docs });
  }

  function readImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error ?? new Error(t.readFailed));
      reader.readAsDataURL(file);
    });
  }

  async function addDoc(file: File) {
    attachError = null;
    attaching = true;
    attachDetail = t.receiving;
    try {
      if (!app.config) throw new Error(t.missingConfig);
      const doc = await parseFileToMarkdown(file, app.config, (evt) => {
        const label = progressLabel(evt);
        if (label) attachDetail = label;
      });
      const projected = totalDocTokens + doc.tokens;
      if (projected > DOC_TOKEN_LIMIT) {
        attachError = t.tooLarge(doc.name, formatTokens(doc.tokens), formatTokens(DOC_TOKEN_LIMIT));
        return;
      }
      // Un .docx ou un PDF non numérisé garde son original : le modèle peut le
      // rendre modifié.
      const original = keepsOriginal(file, doc.ocrUsed) ? await fileToBase64(file) : undefined;
      pendingDocs = [
        ...pendingDocs,
        { name: doc.name, text: doc.markdown, tokens: doc.tokens, ...(original ? { original } : {}) },
      ];
    } catch (e) {
      attachError = e instanceof Error ? e.message : String(e);
    } finally {
      attaching = false;
      attachDetail = null;
    }
  }

  async function handleFiles(files: File[]) {
    for (const file of files) {
      if (file.type.startsWith("image/")) {
        try {
          // Même image collée deux fois : une seule copie part au modèle.
          const url = await readImage(file);
          if (!pendingImages.includes(url)) pendingImages = [...pendingImages, url];
        } catch (e) {
          attachError = e instanceof Error ? e.message : String(e);
        }
      } else {
        await addDoc(file);
      }
    }
  }

  async function onFileChange(e: Event) {
    const target = e.target as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    target.value = ""; // allow re-picking the same file
    await handleFiles(files);
  }

  function removeImage(i: number) {
    pendingImages = pendingImages.filter((_, idx) => idx !== i);
  }

  function removeDoc(i: number) {
    pendingDocs = pendingDocs.filter((_, idx) => idx !== i);
    attachError = null;
  }

  async function onPaste(e: ClipboardEvent) {
    // Tout fichier collé (capture d'écran, ou fichier copié dans l'explorateur :
    // xlsx, pdf, docx…) est traité comme au glisser-déposer. Le texte collé
    // (kind "string") passe dans la zone de saisie comme d'habitude.
    const files = Array.from(e.clipboardData?.items ?? [])
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter((f): f is File => !!f);
    if (files.length === 0) return;
    e.preventDefault();
    await handleFiles(files);
  }

  async function onDrop(e: DragEvent) {
    e.preventDefault();
    dragOver = false;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length > 0) await handleFiles(files);
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit(e);
    }
  }

  function autoResize() {
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 160) + "px";
  }

  function scrollToBottom() {
    queueMicrotask(() => {
      if (scrollArea) scrollArea.scrollTop = scrollArea.scrollHeight;
    });
  }

  function onScroll() {
    if (!scrollArea) return;
    const distanceFromBottom =
      scrollArea.scrollHeight - scrollArea.scrollTop - scrollArea.clientHeight;
    stickToBottom = distanceFromBottom < STICKY_THRESHOLD;
  }

  $effect(() => {
    app.pendingAssistant;
    // La réflexion grossit dans la dernière étape : on la lit pour suivre le bas.
    const last = app.activity[app.activity.length - 1];
    void (last?.kind === "thinking" ? last.text : app.activity.length);
    app.currentMessages.length;
    if (stickToBottom) scrollToBottom();
  });

  const testSuiteCount = $derived(app.config?.testSuite?.length ?? 0);
  const canRunTestSuite = $derived(
    testSuiteCount > 0 && !app.isStreaming && !app.testSuiteRunning,
  );

  async function onRunTestSuite() {
    if (!canRunTestSuite) return;
    // Use whatever is in the composer as a preamble for the first question,
    // then clear it so it isn't left dangling after the run starts.
    const preamble = input;
    input = "";
    autoResize();
    await runTestSuite(preamble);
  }

  let exporting = $state(false);
  const canExport = $derived(
    app.currentMessages.length > 0 && !app.isStreaming && !exporting,
  );

  async function onExport() {
    if (!canExport || !scrollArea) return;
    exporting = true;
    try {
      const conv = app.currentConversationId
        ? app.conversations.find((c) => c.id === app.currentConversationId)
        : null;
      const title = conv?.title?.trim() || t.conversation;
      await exportConversationAsDoc(scrollArea, title);
    } catch (err) {
      app.lastError = err instanceof Error ? err.message : String(err);
    } finally {
      exporting = false;
    }
  }

  // Token estimate for comparison mode — same heuristic and thresholds as the
  // docs tab so the user gets a consistent signal regardless of where they
  // toggle the mode.
  const approxTokens = $derived(
    app.bypassRag ? Math.round(app.activeDocsCharCount / 4) : 0,
  );
  const tokenLimits = $derived(tokenLimitsFor(app.config?.rcpModel ?? ""));
  const sizeWarn = $derived(
    approxTokens >= tokenLimits.hard
      ? "hard"
      : approxTokens >= tokenLimits.soft
        ? "soft"
        : null,
  );

  function formatTokens(n: number): string {
    if (n < 1000) return `${n}`;
    return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)} k`;
  }

  // ── Composer toolbar toggles ───────────────────────────────────────────
  // FullText mode (app.bypassRag) only makes sense with active documents; app
  // state persists it along with them. The other toggles are config
  // preferences, so toggling them from the bar saves the config too.
  const noActiveDocs = $derived(app.activeDocIds.length === 0);
  const hybridOn = $derived(!!app.config?.epflHybridEnabled);
  const exaOn = $derived(!!app.config?.exaWebEnabled);
  const thinkingOn = $derived(!!app.config?.thinkingEnabled);
  // Bulle LEX : cachée sauf si l'usager l'affiche dans la configuration.
  const lexVisible = $derived(!!app.config?.lexButtonVisible);
  const lexOn = $derived(!!app.config?.lexOnlyEnabled);
  const epflConnected = $derived(!!app.oidcSession);

  function toggleFullText() {
    if (noActiveDocs) return;
    app.bypassRag = !app.bypassRag;
  }

  async function toggleHybrid() {
    if (!app.config) return;
    await app.updateConfig({ epflHybridEnabled: !app.config.epflHybridEnabled });
  }

  async function toggleLex() {
    if (!app.config) return;
    await app.updateConfig({ lexOnlyEnabled: !app.config.lexOnlyEnabled });
  }

  async function toggleExa() {
    if (!app.config) return;
    const enabling = !app.config.exaWebEnabled;
    // Warn (synchronously, before any await) about sending queries to an
    // external engine when turning it on.
    if (enabling) app.openExaWebWarning();
    await app.updateConfig({ exaWebEnabled: enabling });
  }

  async function toggleThinking() {
    if (!app.config) return;
    await app.updateConfig({ thinkingEnabled: !app.config.thinkingEnabled });
  }

  const phaseLabel = $derived.by(() => {
    switch (app.retrievalPhase) {
      case "embedding":
      case "searching":
        return t.phaseSearching;
      case "answering":
        return t.phaseAnswering;
      default:
        return t.phasePreparing;
    }
  });
</script>

<section class="chat">
  <div class="controls">
    <span class="tooltip-wrap">
      <button
        onclick={onRunTestSuite}
        disabled={!canRunTestSuite}
      >
        {#if app.testSuiteRunning}
          {t.suiteRunning(app.testSuiteIndex, app.testSuiteTotal)}
        {:else}
          {t.suiteRun(testSuiteCount)}
        {/if}
      </button>
      <span class="tooltip" role="tooltip">
        {#if testSuiteCount === 0}
          {t.suiteNone} <strong>{t.suitePath}</strong> {t.suiteNoneEnd}
        {:else}
          {t.suiteExplain(testSuiteCount)} <strong>{t.suitePath}</strong>.
        {/if}
      </span>
    </span>
    <button
      onclick={onExport}
      disabled={!canExport}
      title={app.currentMessages.length === 0 ? t.exportEmpty : t.exportTitle}
    >
      {#if exporting}
        {t.exporting}
      {:else}
        {t.exportBtn}
      {/if}
    </button>
    <!-- Same affordance as the Outlook add-in: a subtle icon button pushed to
         the right end of the toolbar, only once there is something to reset. -->
    {#if app.currentMessages.length > 0}
      <button
        class="new-conv"
        title={t.newConversation}
        aria-label={t.newConversation}
        disabled={app.isStreaming}
        onclick={() => app.newConversation(app.activeDocIds)}
      >↺</button>
    {/if}
  </div>

  <div class="messages" bind:this={scrollArea} onscroll={onScroll}>
    {#if app.currentMessages.length === 0 && !app.isStreaming}
      <div class="placeholder">
        {#if app.activeDocIds.length === 0}
          <p>{t.noActiveDoc}</p>
          <p>{t.noActiveDocHint} <strong>Docs</strong>{t.docsTabSuffix}.</p>
        {:else}
          <p>{t.askActive}</p>
        {/if}
      </div>
    {/if}

    {#each app.currentMessages as msg (msg.id)}
      <MessageBubble message={msg} />
    {/each}

    {#if app.isStreaming && app.activity.length > 0}
      <AgentActivity steps={app.activity} open={!app.pendingAssistant} live />
    {/if}

    {#if app.isStreaming && app.pendingAssistant}
      <MessageBubble
        message={{
          id: "pending",
          conversationId: app.currentConversationId ?? "",
          role: "assistant",
          content: app.pendingAssistant,
          retrievedChunks: app.pendingRetrieved ?? undefined,
          createdAt: Date.now(),
        }}
      />
    {:else if app.isStreaming}
      <div class="thinking">{phaseLabel}</div>
    {/if}

    {#if app.lastError}
      <div class="error">{app.lastError}</div>
    {/if}
    {#if app.lastWarning}
      <div class="warning" role="status">
        <span>{app.lastWarning}</span>
        <button type="button" onclick={() => (app.lastWarning = null)} aria-label={t.close} title={t.close}>×</button>
      </div>
    {/if}
  </div>

  {#if hasPending || attaching || attachError}
    <div class="attachments">
      {#each pendingImages as src, i (i)}
        <span class="att-thumb">
          <img {src} alt={t.attachmentAlt} />
          <button type="button" class="att-remove" onclick={() => removeImage(i)} title={t.remove} aria-label={t.removeImage}>×</button>
        </span>
      {/each}
      {#each pendingDocs as doc, i (doc.name + i)}
        <span class="att-chip" title={doc.name}>
          📄 <span class="att-name">{doc.name}</span>
          <span class="att-tokens">· ~{formatTokens(doc.tokens)} tk</span>
          <button type="button" class="att-remove" onclick={() => removeDoc(i)} title={t.remove} aria-label={t.removeDoc}>×</button>
        </span>
      {/each}
      {#if attaching}
        <span class="att-status">⏳ {attachDetail ?? t.extracting}</span>
      {/if}
      {#if pendingDocs.length > 0 || convDocTokens > 0}
        <span
          class="att-budget"
          class:warn-hard={totalDocTokens >= DOC_TOKEN_LIMIT}
        >{t.docsBudget(formatTokens(totalDocTokens), formatTokens(DOC_TOKEN_LIMIT))}</span>
      {/if}
      {#if attachError}
        <span class="att-error">{attachError}</span>
      {/if}
    </div>
  {/if}

  <form
    class="composer"
    class:drag-over={dragOver}
    onsubmit={onSubmit}
    ondrop={onDrop}
    ondragover={(e) => { e.preventDefault(); dragOver = true; }}
    ondragleave={() => (dragOver = false)}
  >
    <input
      bind:this={fileInput}
      type="file"
      accept={ACCEPT}
      multiple
      class="file-input"
      onchange={onFileChange}
    />

    <textarea
      bind:this={textarea}
      bind:value={input}
      oninput={autoResize}
      onkeydown={onKeydown}
      onpaste={onPaste}
      placeholder={app.activeDocIds.length === 0 ? t.placeholderNoDocs : t.placeholder}
      disabled={app.isStreaming}
      rows="1"
    ></textarea>

    <div class="toolbar">
      <div class="tools">
        <button
          type="button"
          class="tool-btn"
          onclick={() => fileInput.click()}
          disabled={app.isStreaming || attaching}
          title={t.attachTitle}
          aria-label={t.attachAria}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        </button>

        <button
          type="button"
          class="tool-btn"
          class:on={app.bypassRag}
          onclick={toggleFullText}
          disabled={noActiveDocs}
          aria-pressed={app.bypassRag}
          title={noActiveDocs
            ? t.fullTextNoDocs
            : app.bypassRag
              ? t.fullTextOn(app.activeDocIds.length)
              : t.fullTextOff}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 3h9l4 4v14H6z" />
            <path d="M9 9h7M9 13h7M9 17h4" />
          </svg>
          <span class="tool-label">FullText</span>
        </button>

        <button
          type="button"
          class="tool-btn"
          class:on={hybridOn}
          class:warn={hybridOn && !epflConnected}
          onclick={toggleHybrid}
          aria-pressed={hybridOn}
          title={hybridOn ? (epflConnected ? t.epflOn : t.epflOnNoSession) : t.epflOff}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18" />
            <path d="M12 3a15 15 0 0 1 0 18 15 15 0 0 1 0-18z" />
          </svg>
          <span class="tool-label">EPFL</span>
        </button>

        {#if lexVisible}
          <button
            type="button"
            class="tool-btn"
            class:on={lexOn}
            class:warn={lexOn && !epflConnected}
            onclick={toggleLex}
            aria-pressed={lexOn}
            title={lexOn ? (epflConnected ? t.lexOn : t.lexOnNoSession) : t.lexOff}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3v18M7 21h10" />
              <path d="M4 7h16" />
              <path d="M7 7l-3 7a3 3 0 0 0 6 0zM17 7l-3 7a3 3 0 0 0 6 0z" />
            </svg>
            <span class="tool-label">LEX</span>
          </button>
        {/if}

        <button
          type="button"
          class="tool-btn"
          class:on={exaOn}
          onclick={toggleExa}
          aria-pressed={exaOn}
          title={exaOn ? t.webOn : t.webOff}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <span class="tool-label">Web</span>
        </button>

        <button
          type="button"
          class="tool-btn"
          class:on={thinkingOn}
          onclick={toggleThinking}
          aria-pressed={thinkingOn}
          title={thinkingOn ? t.thinkingOn : t.thinkingOff}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 18h6M10 21h4" />
            <path d="M12 3a6 6 0 0 0-3.5 10.9c.6.4 1 1.1 1 1.8V16h5v-.3c0-.7.4-1.4 1-1.8A6 6 0 0 0 12 3z" />
          </svg>
          <span class="tool-label">{t.thinking}</span>
        </button>

        <button
          type="button"
          class="tool-btn"
          onclick={() => (app.tutorialOpen = true)}
          title={t.helpTitle}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M9.5 9a2.5 2.5 0 1 1 3.4 2.3c-.6.3-.9.8-.9 1.4v.6" />
            <path d="M12 17h.01" />
          </svg>
          <span class="tool-label">{t.help}</span>
        </button>

        {#if app.bypassRag && app.activeDocIds.length > 0}
          <span
            class="size-estimate"
            class:warn-soft={sizeWarn === "soft"}
            class:warn-hard={sizeWarn === "hard"}
          >≈ {formatTokens(approxTokens)} tokens</span>
        {/if}
      </div>

      {#if app.isStreaming}
        <button
          type="button"
          class="send stop"
          onclick={() => app.stopStreaming()}
          title={t.stop}
          aria-label={t.stop}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
        </button>
      {:else}
        <button
          type="submit"
          class="send"
          disabled={(!input.trim() && !hasPending) || attaching}
          title={t.send}
          aria-label={t.send}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M12 5l7 7-7 7" /></svg>
        </button>
      {/if}
    </div>
  </form>
</section>

<style>
  .chat {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  .controls {
    display: flex;
    gap: 0.4rem;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--epfl-gray-100);
    background: var(--epfl-white);
  }

  .controls button {
    background: none;
    border: 1px solid var(--epfl-gray-200);
    padding: 0.35rem 0.7rem;
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
    color: var(--epfl-gray-600);
    cursor: pointer;
    font-family: inherit;
  }

  .controls button:hover:not(:disabled) {
    background: var(--epfl-gray-50);
  }

  .controls button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Icon-only, no frame — it sits at the far right of the toolbar rather than
     reading as a third labelled action. */
  .controls .new-conv {
    margin-left: auto;
    border: none;
    font-size: 1.05rem;
    line-height: 1;
    padding: 0.25rem 0.5rem;
  }

  .messages {
    flex: 1;
    overflow-y: auto;
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    min-height: 0;
  }

  .placeholder {
    color: var(--epfl-gray-400);
    font-size: 0.85rem;
    text-align: center;
    padding: 2rem 0.5rem;
  }

  .thinking {
    color: var(--epfl-gray-400);
    font-size: 0.8rem;
    font-style: italic;
    padding: 0.4rem 0;
  }


  .error {
    background: var(--epfl-danger-bg);
    border: 1px solid var(--epfl-danger-border);
    color: var(--epfl-danger-text);
    padding: 0.5rem 0.7rem;
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
  }

  .warning {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    background: var(--epfl-warning-bg);
    border: 1px solid var(--epfl-warning);
    color: var(--epfl-warning-text);
    padding: 0.5rem 0.7rem;
    border-radius: var(--epfl-radius);
    font-size: 0.8rem;
  }

  .warning span {
    flex: 1;
    overflow-wrap: anywhere;
  }

  .warning button {
    background: none;
    border: none;
    color: inherit;
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }

  .size-estimate {
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    color: var(--epfl-gray-600);
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-100);
    padding: 0.1rem 0.35rem;
    border-radius: var(--epfl-radius);
    white-space: nowrap;
  }

  .size-estimate.warn-soft {
    color: var(--epfl-warning-text);
    border-color: var(--epfl-warning);
    background: var(--epfl-warning-bg);
  }

  .size-estimate.warn-hard {
    color: var(--epfl-danger-text);
    border-color: var(--epfl-danger-border);
    background: var(--epfl-danger-bg);
  }

  .attachments {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 1rem 0;
    background: var(--epfl-white);
  }

  .att-thumb {
    position: relative;
    display: inline-flex;
  }

  .att-thumb img {
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: var(--epfl-radius);
    border: 1px solid var(--epfl-gray-200);
    display: block;
  }

  /* Seul le nom se tronque : la taille et la croix restent toujours visibles,
     même pour un nom de fichier long. */
  .att-chip {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.72rem;
    color: var(--epfl-gray-600);
    background: var(--epfl-gray-50);
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    padding: 0.2rem 0.4rem;
    max-width: 220px;
    white-space: nowrap;
  }

  .att-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .att-tokens {
    flex-shrink: 0;
    color: var(--epfl-gray-400);
  }

  .att-remove {
    border: none;
    background: rgba(33, 33, 33, 0.65);
    color: var(--epfl-white);
    border-radius: 50%;
    width: 15px;
    height: 15px;
    font-size: 0.7rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  /* Croix en haut à droite, sur les vignettes comme sur les documents. */
  .att-thumb .att-remove,
  .att-chip .att-remove {
    position: absolute;
    top: -5px;
    right: -5px;
  }

  .att-status {
    font-size: 0.72rem;
    color: var(--epfl-gray-600);
    font-style: italic;
  }

  .att-budget {
    font-size: 0.7rem;
    font-variant-numeric: tabular-nums;
    color: var(--epfl-gray-600);
    margin-left: auto;
  }

  .att-budget.warn-hard {
    color: var(--epfl-danger-text);
    font-weight: 600;
  }

  .att-error {
    flex-basis: 100%;
    font-size: 0.72rem;
    color: var(--epfl-danger-text);
    background: var(--epfl-danger-bg);
    border: 1px solid var(--epfl-danger-border);
    border-radius: var(--epfl-radius);
    padding: 0.3rem 0.5rem;
  }

  /* Rounded "input card": textarea on top, a tool row underneath, all inside
     one bordered container — mirrors the modern composer layout. */
  .composer {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin: 0.5rem 1rem 0.75rem;
    padding: 0.5rem 0.6rem 0.45rem;
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius-lg);
    background: var(--epfl-white);
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
  }

  .composer:focus-within {
    border-color: var(--epfl-accent);
    box-shadow: 0 0 0 3px rgba(0, 116, 128, 0.12);
  }

  .composer.drag-over {
    background: var(--epfl-gray-50);
    border-color: var(--epfl-gray-400);
    border-style: dashed;
  }

  .file-input {
    display: none;
  }

  textarea {
    resize: none;
    width: 100%;
    box-sizing: border-box;
    padding: 0.3rem 0.4rem;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 0.9rem;
    line-height: 1.4;
    max-height: 160px;
  }

  textarea:focus {
    outline: none;
  }

  textarea:disabled {
    color: var(--epfl-gray-400);
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
  }

  .tools {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    flex-wrap: wrap;
  }

  /* Toolbar buttons: icon-only "+" / attach, icon+label pill toggles. The
     toggles take a Canard border, tint and label when active (.on); the EPFL one
     shows an amber ring when on but not signed in (.warn) since the search needs
     an EPFL session. */
  .tool-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.28rem 0.5rem;
    background: none;
    border: 1px solid var(--epfl-gray-100);
    border-radius: 999px;
    color: var(--epfl-gray-600);
    cursor: pointer;
    font-family: inherit;
    font-size: 0.74rem;
    line-height: 1;
    transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
  }

  .tool-btn svg {
    width: 15px;
    height: 15px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .tool-btn:hover:not(:disabled) {
    background: var(--epfl-gray-50);
    color: var(--epfl-text);
  }

  .tool-btn.on {
    background: var(--epfl-accent-bg);
    border-color: var(--epfl-accent);
    color: var(--epfl-accent);
  }

  .tool-btn.on.warn {
    background: var(--epfl-warning-bg);
    border-color: var(--epfl-warning);
    color: var(--epfl-warning-text);
  }

  .tool-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .tool-label {
    font-weight: 500;
  }

  .send {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.1rem;
    height: 2.1rem;
    flex-shrink: 0;
    padding: 0;
    background: var(--epfl-accent);
    color: var(--epfl-white);
    border: none;
    border-radius: 50%;
    cursor: pointer;
  }

  .send svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .send:hover:not(:disabled) {
    background: var(--epfl-accent-dark);
  }

  .send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .send.stop {
    background: var(--epfl-danger);
  }

  .send.stop:hover {
    background: var(--epfl-danger-text);
  }

  .tooltip-wrap {
    position: relative;
    display: inline-flex;
  }

  .tooltip-wrap .tooltip {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 50;
    background: var(--epfl-text);
    color: var(--epfl-white);
    padding: 0.45rem 0.6rem;
    border-radius: var(--epfl-radius);
    font-size: 0.72rem;
    line-height: 1.35;
    width: 240px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    opacity: 0;
    transform: translateY(-2px);
    pointer-events: none;
    transition: opacity 0.12s ease, transform 0.12s ease;
  }

  .tooltip-wrap .tooltip :global(strong) {
    color: var(--epfl-gray-400);
    font-weight: 600;
  }

  .tooltip-wrap .tooltip::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 1rem;
    border: 5px solid transparent;
    border-bottom-color: var(--epfl-text);
  }

  .tooltip-wrap:hover .tooltip,
  .tooltip-wrap:focus-within .tooltip {
    opacity: 1;
    transform: translateY(0);
  }
</style>
